import React from 'react';
import { Command } from 'cmdk';
import { useHistory } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';

// Navigation items structure
interface NavigationItem {
  id: string;
  label: string;
  icon: string;
  path: string;
  category: string;
  keywords?: string[];
  image?: string;
}

// Mock navigation data from sidebar
const getNavigationItems = (t: any): NavigationItem[] => [
  // Dashboard
  {
    id: 'dashboard',
    label: t('dashboard'),
    icon: 'lucide:layout-dashboard',
    path: '/dashboard',
    category: t('general'),
    keywords: ['home', 'overview'],
  },
  
  // Users Management
  {
    id: 'users',
    label: t('users'),
    icon: 'lucide:users',
    path: '/users',
    category: t('userManagement'),
    keywords: ['people', 'accounts'],
  },
  {
    id: 'permissions',
    label: t('permissions'),
    icon: 'lucide:shield',
    path: '/users/permissions',
    category: t('userManagement'),
    keywords: ['access', 'roles'],
  },
  {
    id: 'permission-groups',
    label: t('permissionGroups'),
    icon: 'lucide:users-round',
    path: '/users/permission-groups',
    category: t('userManagement'),
    keywords: ['groups', 'roles'],
  },
  
  // Portal Sections
  {
    id: 'library',
    label: t('library'),
    icon: 'lucide:book-open',
    path: '/portal-sections/library/books',
    category: t('portalSections'),
    keywords: ['books', 'library'],
  },
  {
    id: 'prayers',
    label: t('prayers'),
    icon: 'lucide:heart',
    path: '/portal-sections/prayers/prayers-list',
    category: t('portalSections'),
    keywords: ['dua', 'prayers'],
  },
  {
    id: 'news',
    label: t('news'),
    icon: 'lucide:newspaper',
    path: '/portal-sections/news/news-list',
    category: t('portalSections'),
    keywords: ['articles', 'updates'],
  },
  {
    id: 'gallery',
    label: t('gallery'),
    icon: 'lucide:images',
    path: '/portal-sections/gallery/media',
    category: t('portalSections'),
    keywords: ['images', 'photos', 'media'],
  },
  {
    id: 'magazines',
    label: t('magazines'),
    icon: 'lucide:book',
    path: '/portal-sections/magazines/magazines-list',
    category: t('portalSections'),
    keywords: ['publications'],
  },
  {
    id: 'wiki',
    label: t('wiki'),
    icon: 'lucide:file-text',
    path: '/portal-sections/wiki/wiki-list',
    category: t('portalSections'),
    keywords: ['encyclopedia', 'articles'],
  },
  {
    id: 'heart-talk',
    label: t('heartTalk'),
    icon: 'lucide:message-circle',
    path: '/portal-sections/heart-talk/heart-talk-list',
    category: t('portalSections'),
    keywords: ['messages', 'conversations'],
  },
  
  // Chat
  {
    id: 'conversations',
    label: t('conversations'),
    icon: 'lucide:message-square',
    path: '/chat/conversations',
    category: t('chat'),
    keywords: ['messages', 'inbox'],
  },
  {
    id: 'organizations',
    label: t('organizations'),
    icon: 'lucide:building',
    path: '/chat/organizations',
    category: t('chat'),
    keywords: ['companies', 'groups'],
  },
];

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
}

const CommandPalette: React.FC<CommandPaletteProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [search, setSearch] = React.useState('');
  
  const navigationItems = React.useMemo(() => getNavigationItems(t), [t]);
  
  // Group items by category
  const groupedItems = React.useMemo(() => {
    const groups: Record<string, NavigationItem[]> = {};
    
    navigationItems.forEach(item => {
      if (!groups[item.category]) {
        groups[item.category] = [];
      }
      groups[item.category].push(item);
    });
    
    return groups;
  }, [navigationItems]);
  
  const handleSelect = (path: string) => {
    history.push(path);
    onClose();
  };
  
  // Reset search when closed
  React.useEffect(() => {
    if (!isOpen) {
      setSearch('');
    }
  }, [isOpen]);
  
  // ESC key handler
  React.useEffect(() => {
    if (!isOpen) return;
    
    const down = (e: KeyboardEvent) => {
      // ESC to close
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    
    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, [isOpen, onClose]);
  
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            onClick={onClose}
          />
          
          {/* Command Palette */}
          <div 
            className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh] px-4"
            onClick={onClose}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: -20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -20 }}
              transition={{ duration: 0.2 }}
              className="w-full max-w-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <Command
                className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-2xl overflow-hidden"
                loop
              >
                {/* Search Input */}
                <div className="flex items-center border-b border-gray-200 dark:border-gray-700 px-4">
                  <Icon icon="lucide:search" className="text-gray-400" width={20} />
                  <Command.Input
                    placeholder={t('searchPlaceholder') || 'Type a command or search...'}
                    value={search}
                    onValueChange={setSearch}
                    className="flex-1 bg-transparent border-none outline-none px-3 py-4 text-base placeholder:text-gray-400 dark:text-gray-100"
                  />
                  <kbd className="hidden sm:inline-flex items-center gap-1 px-2.5 py-1.5 text-sm font-semibold text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded">
                    ESC
                  </kbd>
                </div>
                
                {/* Results */}
                <Command.List className="max-h-[400px] overflow-y-auto p-2">
                  <Command.Empty className="py-12 text-center text-sm text-gray-500">
                    <Icon icon="lucide:search-x" className="mx-auto mb-2 text-gray-400" width={32} />
                    {t('noResults') || 'No results found.'}
                  </Command.Empty>
                  
                  {Object.entries(groupedItems).map(([category, items]) => (
                    <Command.Group
                      key={category}
                      heading={category}
                      className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-gray-500 [&_[cmdk-group-heading]]:dark:text-gray-400"
                    >
                      {items.map((item) => (
                        <Command.Item
                          key={item.id}
                          value={`${item.label} ${item.keywords?.join(' ')}`}
                          onSelect={() => handleSelect(item.path)}
                          className="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-colors aria-selected:bg-primary-100 dark:aria-selected:bg-primary-900/20 hover:bg-gray-100 dark:hover:bg-gray-800"
                        >
                          {/* Icon */}
                          <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                            <Icon icon={item.icon} width={20} />
                          </div>
                          
                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                              {item.label}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                              {item.path}
                            </div>
                          </div>
                          
                          {/* Shortcut hint */}
                          <kbd className="hidden sm:inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 rounded">
                            <Icon icon="lucide:corner-down-left" width={12} />
                          </kbd>
                        </Command.Item>
                      ))}
                    </Command.Group>
                  ))}
                </Command.List>
                
                {/* Footer */}
                <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
                  <div className="flex items-center gap-4">
                    <span className="flex items-center gap-1.5">
                      <kbd className="px-2 py-1 text-xs font-medium bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded">↑↓</kbd>
                      {t('navigate') || 'to navigate'}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <kbd className="px-2 py-1 text-xs font-medium bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded">↵</kbd>
                      {t('select') || 'to select'}
                    </span>
                  </div>
                  <span className="flex items-center gap-1.5">
                    <kbd className="px-2 py-1 text-xs font-medium bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded">ESC</kbd>
                    {t('close') || 'to close'}
                  </span>
                </div>
              </Command>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CommandPalette;
